import React from 'react';

import { UserAvatar, useUserAvatar } from '@/components/UserAvatar';
import UserAvatarGroup from '@/components/UserAvatar/UserAvatarGroup';
import { ChatType, GetChatQuery } from '@/graphql';
import { classNames } from '@/helpers/classNames';
import { getChatTitleMembersRecipient } from '@/helpers/getChatChannelInfo';
import { useAuth } from '@/providers/AuthProvider';
import { formatDistanceToNowStrict } from 'date-fns';

import styles from './ChatHeader.module.scss';

interface IChatHeaderProps {
	chat: GetChatQuery['getChat'];
	onPress?: () => void;
}

export const ChatHeader: React.FC<IChatHeaderProps> = ({ chat, onPress }) => {
	const { user } = useAuth();
	const { title, recipientIds, userIds } = getChatTitleMembersRecipient(
		chat,
		user.id,
		user.displayName,
	);
	const userWithIsActive = useUserAvatar(recipientIds[0]);

	const subtitle =
		userWithIsActive &&
		chat.type !== ChatType.Group &&
		(userWithIsActive.isActive
			? `Active now`
			: getActiveStatusLabel(userWithIsActive.lastSeenAt));

	const isDM = chat.type !== ChatType.Group;

	return (
		<div
			className={classNames([
				styles['chat-header'],
				onPress && styles['chat-header--clickable'],
			])}
			onClick={onPress}
		>
			<div className={styles['avatar']}>
				{renderAvatar(chat, title, recipientIds, userIds)}
			</div>
			<div className={styles['title-row']}>
				<h1 className={styles['title']}>{title}</h1>
				{subtitle && (
					<div className={styles['subtitle']}>{subtitle}</div>
				)}
			</div>
		</div>
	);
};

const renderAvatar = (
	chat: GetChatQuery['getChat'],
	title: string,
	recipientIds: string[],
	userIds: string[],
) => {
	switch (true) {
		case chat.type === ChatType.Group:
			return (
				<UserAvatarGroup
					size="small"
					userIds={[userIds[0], userIds[1]]}
					userAvatarProps={{
						size: 'xx-small',
						activeSize: 'x-small',
						borderColor: 'body-background',
					}}
				/>
			);
		default:
			return (
				<UserAvatar
					size="x-small"
					activeSize="small"
					userId={recipientIds[0]}
				/>
			);
	}
};

const getActiveStatusLabel = (ISOString: string) => {
	try {
		return `Active ${formatDistanceToNowStrict(new Date(ISOString), {
			addSuffix: true,
		})}`;
	} catch (e) {
		return '';
	}
};
